import {
  ActionButton,
  DataObjectCommentsValidFormDataSchema,
  DataObjectCommentsValidFormDataType,
  Detail,
  Form,
  FormAlerts,
  GetApiErrorMessage,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  TextEllipsis,
  useAlertContext,
  useDataObjectCommentsQuery,
  useMutateDataObjectComments,
  useMutationErrors,
} from '@atlas-frontend/sdk';
import { Flex } from 'antd';
import translations from './CommentsModal.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { CommentsTable, CommentsTableProps } from './CommentsTable';
import { useWatch } from 'react-hook-form';

const { addTranslations, t } = i18nNsFactory(
  'visual-validation-comments-modal',
);
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export type WithDescriptionId = {
  descriptionId: number;
};

export const hasDescriptionId = <T extends { descriptionId?: number }>(
  option: T,
): option is T & WithDescriptionId => option.descriptionId !== undefined;

type CommentsModalProps = CommentsTableProps & {
  onClose: () => void;
  refetchDescriptions?: () => Promise<unknown>;
};

export const CommentsModal: React.FC<CommentsModalProps> = (props) => {
  const { descriptionOption, onClose, refetchDescriptions } = props;
  const { data: comments, isFetching } = useDataObjectCommentsQuery(
    descriptionOption.descriptionId,
  );

  const { resetAlert, setSuccess } = useAlertContext();
  const { mutateAsync } = useMutateDataObjectComments();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);

  if (!comments) return null;

  return (
    <Form
      schema={DataObjectCommentsValidFormDataSchema}
      onSubmit={(data: DataObjectCommentsValidFormDataType) => {
        resetAlert();
        clearMutationErrors();
        return mutateAsync({
          id: descriptionOption.descriptionId,
          data,
        })
          .then(async () => {
            // trigger refetch to update hasComments
            await refetchDescriptions?.();
            setSuccess();
            onClose();
          })
          .catch((error) => {
            setMutationErrors(error?.response?.data?.errors ?? []);
          });
      }}
      defaultValues={{
        comments,
      }}
      serverErrors={mutationErrors}
    >
      <CommentsModalInner {...props} isLoading={isFetching} />
    </Form>
  );
};

const CommentsModalInner: React.FC<
  CommentsModalProps & { isLoading?: boolean }
> = (props) => {
  const { descriptionOption, onClose, isLoading } = props;
  const commentCount = useWatch({ name: 'comments' })?.length || 0;

  return (
    <Modal
      onCancel={onClose}
      width="80rem"
      footer={
        <ModalFooter>
          <Flex gap="small" justify="end">
            <ActionButton type="abort" onClick={onClose} />
            <ActionButton type="formSubmit" disabled={isLoading} />
          </Flex>
        </ModalFooter>
      }
    >
      <ModalHeader>
        <ModalTitle>
          <TextEllipsis>{t('title', { count: commentCount })}</TextEllipsis>
        </ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <Flex vertical gap="small">
          <Detail label={t('output')}>{descriptionOption.label}</Detail>
        </Flex>
        <Padding top>
          <CommentsTable {...props} />
        </Padding>
      </Padding>
    </Modal>
  );
};
